import React, { useEffect, useRef, useState } from "react";
import { api, API, authHeaders, noteCreditsRefusal } from "@/lib/api";
import { appUrl } from "@/lib/platform";
import { Send, Sparkles, X, Mic, Square, Copy, Check, Volume2, Loader2, Languages } from "lucide-react";

const sampleQuestions = [
  "Can this be used outdoors?",
  "What sizes are available?",
  "How is it installed?",
  "Is it waterproof?",
];

export default function ChatWidget({ productCode, productName }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      english: `Hi, I'm Heda AI. I can help you learn more about ${
        productName || "this product"
      }. Ask me anything — type or tap the mic to speak in any language.`,
      original: null,
      language: "en",
      language_label: "EN",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, sending]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || sending) return;
    setInput("");
    const newHistory = [...messages, { role: "user", english: q, original: q, language: "auto", language_label: "" }];
    setMessages(newHistory);
    setSending(true);
    try {
      const { data } = await api.post(`/public/products/${productCode}/chat-bilingual`, {
        message: q,
        history: newHistory
          .filter((m) => m.role === "user" || m.role === "assistant")
          .slice(-6)
          .map((m) => ({ role: m.role, content: m.english || m.original || "" })),
      });
      setMessages((m) => [...m, { role: "assistant", ...data }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          english: "Sorry, I couldn't reach Heda AI right now. Please try again.",
          original: null,
          language: "en",
          language_label: "EN",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (blob) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      fd.append(
        "history",
        JSON.stringify(
          messages.slice(-6).map((m) => ({ role: m.role, content: m.english || m.original || "" })),
        ),
      );
      const res = await fetch(`${API}/public/products/${productCode}/chat-voice`, {
        method: "POST",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) {
        await noteCreditsRefusal(res);
        throw new Error(await res.text());
      }
      const data = await res.json();
      setMessages((m) => [
        ...m,
        {
          role: "user",
          english: data.transcript,
          original: data.transcript,
          language: data.language,
          language_label: data.language_label,
        },
        { role: "assistant", ...data, transcript: undefined },
      ]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          english: "Couldn't understand the voice. Please try again or type your question.",
          original: null,
          language: "en",
          language_label: "EN",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating Ask Heda AI dock */}
      <div
        className="fixed bottom-0 inset-x-0 z-40 pointer-events-none"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="pointer-events-auto border-t border-heda-line bg-white/95 px-3 py-3 backdrop-blur">
          <button
            data-testid="chat-toggle-button"
            onClick={() => setOpen(true)}
            className="mx-auto flex w-full max-w-md items-center justify-center gap-2.5 rounded-full bg-heda-ink px-5 py-3 text-[15px] font-semibold text-white shadow-heda transition hover:bg-black"
          >
            <Sparkles size={17} className="text-heda-gold" />
            Ask Heda AI
          </button>
        </div>
      </div>

      <div className="h-20" aria-hidden="true" />

      {open && (
        <div
          className="fixed inset-0 z-50 flex bg-black/40 backdrop-blur-sm md:items-end md:justify-end md:bg-black/20"
          onClick={() => setOpen(false)}
          data-testid="chat-overlay"
        >
          <div
            className="flex h-[100dvh] max-h-[100dvh] w-full flex-col overflow-hidden bg-white md:m-6 md:h-[min(720px,calc(100dvh-3rem))] md:w-[420px] md:rounded-3xl md:border md:border-heda-line md:shadow-2xl"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 border-b border-heda-line bg-white px-4 py-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-heda-gold/30 bg-heda-gold/10 text-heda-gold">
                  <Sparkles size={15} />
                </span>
                <div className="min-w-0">
                  <div className="font-display text-[15px] font-bold text-heda-ink">Ask Heda AI</div>
                  <div className="truncate text-[11.5px] text-heda-mute">{productName || "Product specialist"}</div>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="hed-icon-btn"
                data-testid="chat-close-button"
              >
                <X size={18} />
              </button>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, idx) => (
                <ChatBubble key={idx} message={m} />
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="hed-typing bg-heda-cream border border-heda-line px-4 py-3 rounded-2xl rounded-bl-md">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              {messages.length <= 1 && !sending && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {sampleQuestions.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      className="hed-chip cursor-pointer bg-white py-1.5 transition hover:border-heda-gold hover:text-heda-gold"
                      data-testid="chat-sample-question"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              className="flex w-full items-center gap-2 border-t border-heda-line bg-white p-3"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
              onSubmit={(e) => { e.preventDefault(); send(); }}
            >
              <MicButton onAudio={sendVoice} disabled={sending} />
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type or tap the mic…"
                className="min-w-0 flex-1 rounded-full border border-heda-line bg-heda-cream/50 px-4 py-2.5 text-base outline-none placeholder:text-heda-mute focus:border-heda-gold"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={sending || !input.trim()}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-heda-ink text-white transition hover:bg-black disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// ============================================================
// ChatBubble — handles bilingual toggle, copy, TTS
// ============================================================
export function ChatBubble({ message }) {
  const isUser = message.role === "user";
  const hasBilingual =
    !isUser && message.original && message.language && message.language !== "en";
  const [showEnglish, setShowEnglish] = useState(false);
  const display = showEnglish || !message.original ? message.english : message.original;

  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(display || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const onPlay = async () => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
      return;
    }
    setPlaying(true);
    try {
      const res = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ text: display }),
      });
      if (!res.ok) {
        await noteCreditsRefusal(res);
        throw new Error("tts");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch {
      setPlaying(false);
    }
  };

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={isUser ? "max-w-[80%]" : "max-w-[88%]"}>
        <div
          data-testid={`chat-message-${message.role}`}
          className={
            isUser
              ? "whitespace-pre-line rounded-2xl rounded-br-md bg-heda-ink px-3.5 py-2.5 text-[14px] leading-relaxed text-white"
              : "whitespace-pre-line rounded-2xl rounded-bl-md border border-heda-line bg-heda-cream px-3.5 py-2.5 text-[14px] leading-relaxed text-heda-ink"
          }
        >
          <RenderWithLinks text={display || ""} />
        </div>
        {!isUser && (
          <div className="mt-1.5 ml-1 flex items-center gap-1 flex-wrap">
            {hasBilingual && (
              <button
                onClick={() => setShowEnglish((x) => !x)}
                title={showEnglish ? "Show original language" : "Show in English"}
                className="hed-mini-btn uppercase tracking-wider"
                data-testid="bilingual-toggle"
              >
                <Languages size={10} />
                {showEnglish ? message.language_label : "EN"}
              </button>
            )}
            <button
              onClick={onCopy}
              title="Copy this answer"
              className="hed-mini-btn uppercase tracking-wider"
              data-testid="chat-copy"
            >
              {copied ? <Check size={10} /> : <Copy size={10} />}
              {copied ? "Copied" : "Copy"}
            </button>
            <button
              onClick={onPlay}
              title="Play this answer"
              className="hed-mini-btn uppercase tracking-wider"
              data-testid="chat-play"
            >
              {playing ? (
                <Loader2 size={10} className="animate-spin" />
              ) : (
                <Volume2 size={10} />
              )}
              {playing ? "Playing" : "Play"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Render assistant text with markdown links [text](/url) becoming clickable React Router links.
function RenderWithLinks({ text }) {
  if (!text) return null;
  const parts = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(<span key={key++}>{text.slice(last, m.index)}</span>);
    parts.push(
      <a
        key={key++}
        href={m[2].startsWith("/") && !m[2].startsWith("//") ? appUrl(m[2]) : m[2]}
        className="font-semibold text-heda-gold underline underline-offset-2 hover:text-heda-ink"
      >
        {m[1]}
      </a>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(<span key={key++}>{text.slice(last)}</span>);
  return <>{parts}</>;
}

// ============================================================
// MicButton — tap to record / tap again to stop using MediaRecorder
// ============================================================
function MicButton({ onAudio, disabled }) {
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      recRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        if (blob.size > 1000) onAudio(blob);
      };
      mr.start();
      setRecording(true);
    } catch (e) {
      alert("Microphone access denied. Please allow microphone access to use voice chat.");
    }
  };
  const stop = () => {
    if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={disabled && !recording}
      data-testid={recording ? "chat-mic-stop" : "chat-mic-start"}
      className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition disabled:opacity-40 ${
        recording
          ? "animate-pulse bg-red-700 text-white hover:bg-red-800"
          : "border border-heda-line bg-heda-cream text-heda-ink hover:border-heda-gold hover:text-heda-gold"
      }`}
      title={recording ? "Stop recording" : "Voice input"}
    >
      {recording ? <Square size={14} /> : <Mic size={16} />}
    </button>
  );
}
